import { Component, Injector, Input, OnInit } from '@angular/core';
import { AlertService, ApiCollection, ChassisFormHandler, Collection, ModalService, TenantModel, trans } from '@kaiser-software/chassis-core';
import { Subject } from 'rxjs/Subject';
import { TenantLocationService } from '../../services/tenant-location.service';
import { LocationModel } from '../../models/location.model';
import { CreateLocationModal } from '../../modals/create-location/create-location.modal';
import { EditLocationModal } from '../../modals/edit-location/edit-location.modal';

@Component({
    selector: 'eyewatch-location-list-plugin',
    templateUrl: 'location-list.plugin.html',
    styleUrls: [ 'location-list.plugin.scss' ],
    providers: [ TenantLocationService ]
})
export class LocationListPlugin implements OnInit {

    public items: Collection;

    /**
     * Chassis form handler instance injected by the plugin
     * slot, containing the form handler from the page.
     */
    @Input() public form: ChassisFormHandler;

    /**
     * Observable containing the page's model instance.
     */
    @Input() public model: Subject<TenantModel>;

    private modals: ModalService;

    private alerts: AlertService;

    private tenantLocationService: TenantLocationService;

    constructor(injector: Injector) {
        this.modals                = injector.get(ModalService);
        this.alerts                = injector.get(AlertService);
        this.tenantLocationService = injector.get(TenantLocationService);
    }

    ngOnInit() {
        this.model.subscribe((model: TenantModel) => {
            if (model.exists()) {
                this.tenantLocationService.defaultParams = { tenant: model.id };
                this.tenantLocationService.all().subscribe((items: ApiCollection) => {
                    this.items = new Collection(items.all());
                });
            } else {
                this.form.success((model: TenantModel, resolve, reject) => {
                    this.tenantLocationService.defaultParams = { tenant: model.id };
                    this.postItemsAfterParentCreated(resolve, reject);
                });
            }
        });
    }

    /**
     * Post all items after the model in the main form has
     * been created.
     *
     * @param resolve
     * @param reject
     */
    private postItemsAfterParentCreated(resolve, reject) {
        if (!this.items.length) {
            resolve();
            return;
        }

        this.items.forEach((location: LocationModel, index: number) => {
            if (!location.exists()) {
                this.model.subscribe((model: TenantModel) => {
                    // Set the default parent tenant id
                    location.parent_id = model.id;

                    location.save().subscribe(() => {
                        if (index === this.items.length - 1) {
                            resolve();
                        }
                    }, () => {
                        reject();
                    });
                });
            }
        });
    }

    /**
     * Open the add modal.
     */
    public openAddModal() {
        this.model.subscribe((tenant: TenantModel) => {
            if (tenant.exists()) {
                const modal = this.modals.open(CreateLocationModal, {
                    tenant: tenant,
                });

                modal.closing((resolve, reject, item) => {
                    item.save().subscribe((result) => {
                        resolve(result);
                    }, (error) => {
                        reject(error);
                    });
                });

                modal.result.then((result: LocationModel) => {
                    this.items.push(result);
                }, (reason) => {
                });
            }
        });
    }

    /**
     * Open the edit modal.
     *
     * @param {LocationModel} model
     */
    public openEditModal(model: LocationModel) {
        this.model.subscribe((tenant: TenantModel) => {
            const modal = this.modals.open(EditLocationModal, {
                tenant: tenant,
                location: model.clone(),
            });

            modal.closing((resolve, reject, result) => {
                return this.beforeCloseCallback(resolve, reject, result);
            });

            modal.dismissing((resolve, reject, reason) => {
                console.log(reason);
                if (reason === 'delete') {
                    return this.beforeDismissDeleteCallback(resolve, reject, reason, model);
                } else {
                    resolve(reason);
                }
            });

            modal.result.then((result: any) => {
                model.fill(result);
            }, (reason) => {
                if (reason === 'delete') {
                    this.items = this.items.filter((item) => {
                        return item !== model;
                    });
                }
            });
        });
    }

    /**
     * Saves the instance returned from the modal.
     *
     * @param {Function} resolve
     * @param {Function} reject
     * @param {LocationModel} tenant
     */
    private beforeCloseCallback(resolve: Function, reject: Function, tenant: TenantModel) {
        if (tenant.exists()) {
            tenant.save().subscribe((result) => {
                resolve(result);
            });
        } else {
            resolve(tenant);
        }
    }

    /**
     * Deletes the instance returned from the modal.
     *
     * @param {Function} resolve
     * @param {Function} reject
     * @param reason
     * @param {LocationModel} tenant
     */
    private beforeDismissDeleteCallback(resolve: Function, reject: Function, reason: any, tenant: LocationModel) {
        const prompt = this.alerts.open({
            title: trans('Are you sure?'),
        });

        prompt.result.then(() => {
            if (tenant.exists()) {
                tenant.delete().subscribe(() => {
                    resolve(reason);
                }, (error) => {
                    reject(error);
                });
            }
        }, () => {
            reject();
        });
    }

}



// WEBPACK FOOTER //
// ./apps/eyewatch/src/app/eyewatch/locations/plugins/location-list/location-list.plugin.ts